import { or, and, not, createMachine } from 'xstate';

import { routePath } from 'config/route-config';
import { actionUrl } from 'config/static-content';
import { events } from 'machines/config/events';
import { guards } from 'machines/config/guards';
import { stringToBool } from 'utils/misc';

export const voiceSalesAquisitionMachineConfig = {
  id: 'voiceSalesAquisitionMachine',
  context: {
    guards: {}
  },
  initial: 'home',
  states: {
    home: {
      on: {
        NEXT: [
          {
            target: 'fuelType',
            guard: 'hasAnyMeterValidationRequired'
          },
          {
            target: 'selectOptions',
            guard: 'hasMeterExceptions'
          },
          {
            target: 'mixedMeters',
            guard: 'hasMixedMeters'
          },
          {
            target: 'personaliseQuote'
          }
        ],

        BLOCK_JOURNEY: {
          target: 'businessUpdatesAnnouncement'
        }
      },
      meta: {
        path: routePath.voicesalesAcquisition
      }
    },

    businessUpdatesAnnouncement: {
      on: {
        NEXT: {
          target: 'businessUpdatesSubmitEmail'
        },
        ALLOW_JOURNEY: {
          target: 'home'
        }
      },

      meta: {
        path: routePath.businessUpdatesAnnouncement
      }
    },

    businessUpdatesSubmitEmail: {
      on: {
        NEXT: {
          target: 'businessUpdatesSubmitEmailSuccess'
        }
      },
      meta: {
        path: routePath.businessUpdatesSubmitEmail
      }
    },

    businessUpdatesSubmitEmailSuccess: {
      meta: {
        path: routePath.businessUpdatesSubmitEmailSuccess
      }
    },

    fuelType: {
      on: {
        NEXT: [
          {
            target: 'meterpointValidation',
            guard: 'meterValidationRequired'
          },
          {
            target: 'selectOptions',
            guard: 'hasMeterExceptions'
          },
          {
            target: 'personaliseQuote'
          }
        ],
        CHANGE_ADDRESS: {
          target: 'home'
        }
      },
      meta: {
        path: routePath.fuelType
      }
    },

    meterpointValidation: {
      on: {
        NEXT: [
          {
            target: 'selectOptions',
            guard: 'hasMeterExceptions'
          },
          {
            target: 'mixedMeters',
            guard: 'hasMixedMeters'
          },
          {
            target: 'personaliseQuote'
          }
        ],
        GUIDE_CLICK: {
          target: 'meterPointGuide'
        }
      },
      meta: {
        path: routePath.meterpointValidation
      }
    },

    meterPointGuide: {
      meta: {
        path: routePath.meterpointGuide
      }
    },

    selectOptions: {
      on: {
        ELIGIBLE_FUEL: {
          target: 'personaliseQuote'
        },
        EDIT_ADDRESS: {
          target: 'home'
        },
        HOME_MOVE: [
          {
            target: 'externalContactUs',
            description: `or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])`,

            guard: or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])
          },
          {
            target: 'externalMoveIn',
            guard: 'isBGECustomer'
          },
          {
            target: 'externalBgClassicHomeMove'
          }
        ]
      },
      meta: {
        path: routePath.selectOptions
      }
    },

    mixedMeters: {
      on: {
        NEXT: {
          target: 'personaliseQuote'
        }
      },
      meta: {
        path: routePath.mixedMeters
      }
    },

    personaliseQuote: {
      on: {
        NEXT: [
          {
            target: 'evTariffInformation',
            guard: 'showEvTariffInformation'
          },
          {
            target: 'paymentMethod',
            guard: 'isPrePayOnly'
          },
          {
            target: 'consumptionEntry'
          }
        ]
      },
      meta: {
        path: routePath.personaliseQuote
      }
    },

    evTariffInformation: {
      on: {
        NEXT: [
          {
            target: 'consumptionEntry'
          }
        ]
      },
      meta: {
        path: routePath.evTariffInformation
      }
    },

    paymentMethod: {
      on: {
        NEXT: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.prepayInfo
      }
    },

    consumptionEntry: {
      on: {
        NEXT: [
          {
            target: 'quote',
            guard: 'isEligibleCustomer'
          },
          {
            target: 'personalInformationSummary'
          }
        ]
      },
      meta: {
        path: routePath.consumptionEntry
      }
    },

    personalInformationSummary: {
      on: {
        NEXT: {
          target: 'home'
        },
        CHANGE_ADDRESS: {
          target: 'home'
        },
        CHANGE_CONSUMPTION: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.personalInformationSummary
      }
    },

    quote: {
      on: {
        NEXT: {
          target: 'tariffDetails'
        },
        CONSUMPTION: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.quote
      }
    },

    tariffDetails: {
      on: {
        NEXT: {
          target: 'createAccount'
        }
      },
      meta: {
        path: routePath.tariffDetails
      }
    },

    createAccount: {
      on: {
        NEXT: [
          {
            target: 'directDebitType',
            description:
              "and(['hasMultipleDirectDebitTypes', 'isAnyDirectDebit'])",
            guard: and(['hasMultipleDirectDebitTypes', 'isAnyDirectDebit'])
          },
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'psrEntry'
          }
        ],
        CHANGE_ADDRESS: { target: 'home' }
      },
      meta: {
        path: routePath.createAccount
      }
    },

    directDebitType: {
      on: {
        NEXT: {
          target: 'directDebit'
        }
      },
      meta: {
        path: routePath.directDebitType
      }
    },

    directDebit: {
      on: {
        NEXT: {
          target: 'psrEntry'
        }
      },

      meta: {
        path: routePath.directDebit
      }
    },

    psrEntry: {
      on: {
        NEXT: [
          {
            target: 'psrCapture',
            guard: 'hasPSR'
          },
          {
            target: 'userPreferences'
          }
        ]
      },
      meta: {
        path: routePath.psrEntry
      }
    },

    psrCapture: {
      on: {
        NEXT: {
          target: 'userPreferences'
        }
      },
      meta: {
        path: routePath.psrCapture
      }
    },

    userPreferences: {
      on: {
        NEXT: {
          target: 'orderSummary'
        }
      },
      meta: {
        path: routePath.userPreferences
      }
    },

    orderSummary: {
      on: {
        NEXT: {
          target: 'orderComplete'
        }
      },
      meta: {
        path: routePath.orderSummary
      }
    },

    orderComplete: {
      meta: {
        path: routePath.orderComplete
      }
    },

    authResponse: {
      meta: {
        path: routePath.authResponse
      }
    },

    externalContactUs: {
      meta: { path: actionUrl.contact }
    },
    externalBgClassicHomeMove: {
      meta: { path: actionUrl.bgClassicHomeMoveUrl }
    },

    externalMoveIn: {
      meta: { path: actionUrl.moveIn }
    }
  },
  ...events
};

const { VITE_XSTATE_DEBUG } = process.env;
const debug = stringToBool(VITE_XSTATE_DEBUG);
// for VS extesnion
/* istanbul ignore next -- @preserve */
debug &&
  createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QDcD2BLAxmAygQwBs4BBARwFd1Z0AXdVAOwFk9MALdBsAOjdQFswAYgByAUQAaAFQDaABgC6iUAAdU1Oo2UgAHogCMAdgBMx7gFY5+4wA4bANhvmAzHJvOANCACeB-c8MLY0dHNwBOQzlDcwBfGK80LFxCEgoqWnpmVg4uXgFhcWkZfSUkEDUNTO09BCNTCytbBydXdy9fBBMbbht9fTDHfTljAf04hIxsfCJYMkpKxhZ2Th4+QSEAIQAZAHkAYQBpAH0AKR2AVQAlcQBNeVLVdQytMpq6s0trO0cXN08fRABfTcCIAFmMRkszl6YRs4xAiSmKVmaQWWWWuQARuRqFxYLBzioIHgaCQGAxUOQGNhBAwaKJJLJFNoKs8GNVEKZodwhoZ9KC5PZDDYrP8Os57PZuM4wsF9PZzH0hdF4YjkjM5ulNOicjxsbi4ASiSSyRSqTSwHShMQtrsAOqnC7XMR3ZllVnajm1QygwL8gb2X18+wS9qA+r8hwjUE2cEx0HOVWTdWpeZspa67j6lb4wnE0mwHDkTH8Whifh4dAEBlFN2PNFe2xGbii0GBoyy2xiwGS6Wy+zyxXywwq+II5PTVNazIZlbcABm5DABCk3hUBUZ9xZT09r05xm5vP5guForDnWC3DbriilkMQvMhiTSUnKLT2tnuUXy9X65rshKbcGz3BAuW6I8BSFEV-HPGwBm4SI+kiew5GcA8wkTMc1VfTU0U-HhvxXNcNyKYwHnKHcqhAsCeSiY8oLPAEEBjKURXMRUwlhdxzAiZ8kQ1VF02yOdCN-YQ9gACWIEQAHExCOYgABFFMuMQcBwLd3Uol5QDeQxnHMCwYxcBUB2+c8oJBBxoTkP5oV4rCJ2RXChIxHhBFJAAnNROBoAA1Qh0HzTJ-00+s2UbXpAlbdtDE7NomJHKUmgCBUMNBFwn0cl9nMEj9hNyDywG8jA6QCgggpJELCgA8iPSo3TOSilt-DbUEO1MBKOhMKVr1s6IogfLKJhygT3xnAr3LALyfLKwLgsYIQZPOABJRT5L2LYVsOMKKOAxrQOamL2rizruwQFDAghGxjDkDLnGsGNhvHUapzwybuFgZcwEwGgdhUbVYCEMQtpklbtnkgAxc4Qd2+qdN0QEMrMEdzAhNsbvasJz040Er1cIMHqiDDjD4lM32nRYPq+ohfv+wHgcUlapAU5TVPUuHtPZajZTkHlzBFWFrHMZHQXPIZmz6GVAwHVxjFibL+Le1zMxpn6-oBzIgYknYmHkpgdj8sROf2xGEGcZGEOidHHGMLHxbkMI8bcfkLfagdwWe7DcvGqm3M+766c1xhtd1-XDeNwCtNNmoLbRq20f5W37aY6x+SvKwbtjIUwkdsZFfJlz8v9tWg4ZnW9aOA2jZkMigIikC45R62k8x-oHdBPH5ZQhVnFcCVO7JnC8om-2VDwbxaRoJhpr4CBQrrPaG4OgJXG4HPzBQhM203wxYOBNCCaTtsRzQoefcpnU50DmhPKwPywA4TAiAXuqucbeVuC7GxhVMSJjDiuLGMzhpSQh9ChNCYR5TnzGpffC3Ab530wA-J+L8arFDfjHTkn9v6-wAcMQBqcRhhAsO4SEDgCFOBgcrYumZEH30flgF+Jtl5mwhMldwP8br4IAdjVOFsSECh-iGH+sJQScWoRTd6-swDICkHgO+855wrQYPOVAnkKzalfvXXcB0HCGQTOAu6vorB71TsA0B0QjGQOgQXYevsr65EwCHcg-Bg4MDEHSTy3htHR1YW8PuICAxWHlO7PuZiOj8hHAhAyEQJR9H9JhEaSspEqznM4hgsBXHuM8bfHx6Co7hV0WbOCeNAwPWFHBewnFFTi2sCQuOjR7y+iiZIouo9Mzrk8rARghAVFqI0VVRgRZ+AVm8b4opDUSkKnxkYzuiEImIH0lKQUjR7DRh-hbNpI8-adOKj0hgfTVHqM0ZkEZYyfGSWknJVmKk1IaUXvDbmeiZmGLbMYhZFkHBfwtlY26ucwguG2Q4+BXSDlHIGac4ZriLlCCubJKGMMthHCkDcAACsbR578QL6Nme8+Zpjzy3WBPpAWBlfSd3WT-YFcCPpgt6QQfpJyhkMHOQoy5UkEVHD2DsEQOBzhMDRVIFavKWHFJqLit5W8TFGDqXFHkkRoT6CcHdDCNLpGZgoKgUkEyl7ioMCYD4jRvgtD+OLX4IIpaCnKahdVaTchap1WIJgxAVrIoAIrnB2FITFmD-EGvqJ8JoPxWjnSid0WyBlTCDDRjGO1tC5yOvEry-lgrhWiqxVg2o4Jkq51cAMGUfIIhAIAZa1CTgeL6EfHCOxF8NWJvINq4Q0NYaZv9d6QNxrmi-C6gYAI3Qo0W0lOCaEEJ40dLnDQBR6AlGKWmpWAgQN0Ftv1aBA84E6KQVPDBVOFqoH+GtQmW1tbYH1tyGACsVZ3WNp1cuv1q7lXcmqQAtCgY5BowtiW8CI50qSn6C4BWyTC47McTwTAnkwAmmIJgZxVJ6R3p0VMmoTZoqtVivFMNaNDKQJYnySITtQTjt2ekiDUGYOUitPCm5Sk7kcxXUhzkFsQEi1cEhOKIZxby0CBhQFaMQzywDER0D3AIDoAg79OdmJaBiV1U8r0TcE42zbnwyJLgSWUplBEEMIYkkvRSe04juRRPiZoJJ6TxEhBioY+bS2qMlN23buYgyV4mwDAFkKO2YQhPwOM+rMz8HNz0YRjUdqn9IhxV6PKCWNhzVr33a4Yd-dvMfV8xJsAUn6RWeC4gULUpwtCyi5nIBIsWwYR+P0D2Cpktj1gJ5XJ4yEN+NXZ3B6X8oG3WqeszuMXzGO3Xr3WEB4Ah3Wq502r9X8mbkKXq6zLXgQjGsIKAYdsYyxb5lEVGhieKoUIyemhE7cgqFq3sPAANyAQdk9ig6vpYw9BlCfXOko+RAL65KFwg3wkjb26khNuQcTFTRRB+cxVLTYCXYF+91nLA8QsHx-oRMBzGHNfUG6TsHB4cdoPb7BnhPqIgMVNlDWIeIeywgFwB4v6RHEZ7B6wRkdmFR7howmPdtjgpPj+AZRvanvtWAEnzyzYAFp7DnkF4ZTiEvJcS7grp7n+3DOrHyPzr08pUI8ljJEdiQxOI7u6rYEEEXxGSidgeUm2OQPwOzHiI0+ZTQUYtHSZXIF-3Aj6L6apj0mwi6YhKKUm9VnRAyqhdZo25xW8NHmE0hZiylhoOWBdTuDry3EbRAYucWnBAyueX3Fge63iDxKM3QH7G0v9uH3MxoCxFhLGWS9BAiwwcNIns2D106B4lBhe8AxFnm17P7y6IsoQh-NyCj6oliLN+Q8MMwqrjJdltj1vXUoQxuDglEdqAQvZOR579qaM1Sr+XmiyyfnIeKGR-j6XknWTznh6uvVoa-L+b9D4VaaAOD8yUoPjk-5s3cIRY9UqlN3hZFENKBQhQhEHyI7C-jwKXBrIDD-g9JxNKJEH8CYKOovn4PLOvDdlBIqv4PYDAdwOPJPJaNPLPKgBAIgS0LRH0NtpYFnOdINrDlWvogLCMDxEQfQsgows-Hzk1rNkjvwo+F-Oun0KhFWnFLLtvvLsJrIvIoososcoMsUnJiBOsiAvpOIulH8BwUAu4FeC0Bpj6FjsXnWrztwBklkm4tqBNj-m7iAt3OCCKI7J1mGm2CQtwhKBKB+gqDWmYTvgdjwPSocoysoVCqyjCuyj-uwT0GjOjnGAqPeLftyF0EhGhDdGhPnAEbIfAkmvYShDPvuiMO4A+PLEAiYNKKYIKHBOjo+NkXpsBqPv7FOooXOlOlWJzpMqTrdPUH+rdALBEBEKYLFsCMTMQpftBP4Y0SXmejwBegutek2vYYCq7lEh7nbF7hUR8FUp3nYGjkQeBpBqSNBrBo7gIT0WSj0ChK4Z3DxL0OYMjgYmCCOANPdEQalqZuluZuuIgddCCI4NEPUcAhxrutgeIj6K8ZYO8SPqXpmJ8f5ogVAoZENkbu1O9sWmCWUu2J3CdH0LCEQUdnVl4h0N0QLiFg9IZHyDdNYBKM0EIaphTu4O+qEsqkYNMXLj9kEcQcdqdjQOdvwWSV6G1CQung5gAtdAyQYAKJ4X3MzoEsEA5DkVyQrtwP9p5IDmAMDhBtSHAD-jGD-F-AONamjLYAmPTiCC8ajHdECrCXMdwHjgTlEd4jEVFgbjvI+E7AEBaZxJCdaTCcqTjvAo6Z5HsAICoEQKSDEeCIZM+r8NEI4LGI8WCWYL6UGG8baYGRbh9GADoF5KEWGXSKwDQOcF0TNqTtCC4LQVWrnAwV2HUiMFeAMFnB6VxFwXmcVKERsFAHsAQHgPiFgBJPkEwKgMgIKeWeSYCDQRLDWe+m4PWanMYi5gKJvN4egQ0ZyUGTmR2Z5KESOWOSotQVWTOfQfOb2rUAeHzA9E4GnEtrdJhHEEAA */
      ...voiceSalesAquisitionMachineConfig
    },
    {
      guards
    }
  );
